import { ArrowRight, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { apiMode } from '../api';
import { money } from '../format';
import { useJourney } from '../journey';
import { payEquity } from '../paymentSdk';
import { useOffer } from '../useOffer';
import Button from '../ui/Button';
import { AmountBanner, Badge, Copyable, DetailRows, Loading, Notice, SectionTitle, StatusCard } from '../ui/Parts';
import Shell from '../ui/Shell';

/** Make Your Upfront Payment: the equity, paid to Ceviant Finance through Ceviant's payment SDK. */
export const UpfrontPayment = () => {
  const { steps, current, session, updateUi, refresh, close } = useJourney();
  const { offer } = useOffer(true);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState('');
  const equity = session.equityPayment;
  const currency = equity?.currency ?? session.currency;

  const pay = async () => {
    if (!equity) {
      return;
    }
    setError('');
    setOpening(true);
    try {
      await payEquity({
        mode: apiMode(),
        applicationReference: session.applicationReference,
        equity,
        customer: session.customer,
        onPaid: (reference) => {
          updateUi({ awaitingPaymentConfirmation: true, paymentReference: reference });
          refresh();
        },
        onClose: () => setOpening(false),
      });
    } catch (e: any) {
      setError(e?.message ?? 'The payment could not be started. Please try again.');
      setOpening(false);
    }
  };

  return (
    <Shell
      steps={steps}
      current={current}
      onClose={close}
      footer={
        <Button onClick={pay} loading={opening} disabled={!equity}>
          Pay Now <ArrowRight className="h-5 w-5" aria-hidden />
        </Button>
      }
    >
      <SectionTitle title="Make Your Upfront Payment" subtitle="Pay your share of the purchase to continue." />
      {equity?.status === 'FAILED' && <Notice>{equity.message ?? 'Your last payment did not go through. Please try again.'}</Notice>}
      {error && <Notice>{error}</Notice>}
      {equity && <AmountBanner label="Amount to pay" amount={money(equity.amount, currency)} />}
      <DetailRows
        title="Payment Summary"
        rows={[
          { label: 'Purchase amount:', value: money(offer?.purchaseAmount ?? session.purchaseAmount, currency) },
          { label: 'Your upfront payment:', value: money(offer?.equityAmount ?? equity?.amount, currency) },
          ...(offer ? [{ label: 'Financed amount:', value: money(offer.financedAmount, currency) }] : []),
        ]}
      />
      <p className="mt-3 flex items-center gap-1.5 text-xs text-lend-body">
        <ShieldCheck className="h-4 w-4 text-lend-blue" aria-hidden />
        Your payment will be processed securely through the available payment service
      </p>
    </Shell>
  );
};

/** The customer finished paying; the gateway has not confirmed it to the backend yet. */
export const ConfirmingPayment = () => {
  const { steps, current, close } = useJourney();
  return (
    <Shell steps={steps} current={current} onClose={close}>
      <div className="text-center py-6">
        <Loading />
        <h2 className="text-lg md:text-xl font-semibold">Confirming your payment</h2>
        <p className="mt-2 text-sm md:text-base text-lend-body">This usually takes a few seconds. Please keep this window open.</p>
      </div>
    </Shell>
  );
};

export const PaymentSuccessful = () => {
  const { session, ui, updateUi, close } = useJourney();
  const equity = session.equityPayment;
  return (
    <Shell onClose={close} footer={<Button onClick={() => updateUi({ paymentSuccessSeen: true })}>Continue</Button>}>
      <StatusCard tone="success" title="Payment Successful" subtitle={'Your upfront payment has been received. Click "Continue" to move forward'}>
        <DetailRows
          rows={[
            { label: 'Amount paid:', value: money(equity?.amount, equity?.currency ?? session.currency) },
            ...(ui.paymentReference ? [{ label: 'Payment reference:', value: <Copyable value={ui.paymentReference} /> }] : []),
            { label: 'Status:', value: <Badge>Successful</Badge> },
          ]}
        />
      </StatusCard>
    </Shell>
  );
};
